import L from 'leaflet';
// Attaches L.markerClusterGroup to the Leaflet namespace as a side effect; the
// package exports nothing of its own, which is why this import has no binding.
import 'leaflet.markercluster';

// The customers this candidate can reach, drawn on the Karte section of the
// candidate page (D15). The mirror of kunden/kundeKarte.js, which draws the
// candidates around a customer.
//
// The template decides whether there is anything to draw: a candidate with no
// coordinates, no means of transport, or no travel time gets a German hint
// instead of a map container, so this module finds no element and returns. That
// is the whole fallback - there is no empty grey tile box to hide, because the
// box is never rendered.

// Mapbox's raster tile endpoint, drawn by Leaflet as a plain XYZ layer. The same
// choice kundeKarte.js makes and for the same reason: mapbox-gl-js is 200 KB+
// gzipped and esbuild builds one file with no code splitting, so it would
// roughly double the bundle app-wide.
const TILE_URL =
  'https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/256/{z}/{x}/{y}?access_token={accessToken}';

// Required by Mapbox's terms and by OpenStreetMap's licence - the underlying
// data is theirs.
const ATTRIBUTION =
  '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> ' +
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

// Wider than the customer map's 16. That one centres on a single address and
// wants the buildings around it; this one has to show a whole commutable region,
// so it opens at roughly canton scale and the fit below usually overrides it.
const ZOOM = 10;

// The region the results fragment replaces, watched so the pins stay in step
// with the table.
//
// Same mechanism as the customer map: the map container sits OUTSIDE this
// region, because liveList.js replaces the region's innerHTML and Leaflet would
// be torn out of the DOM on every filter change - rebuilt, flickering, losing
// the zoom somebody set and costing a fresh round of tile requests. So the rows
// and the pins arrive together in one response and cannot disagree.
const ERREICHBAR_REGION = 'erreichbare-kunden';

// The candidate's own position, distinct from the customer pins: there is one of
// these and many of those, and the eye has to find the address the page is about.
const KANDIDAT_MARKER = {
  radius: 9,
  weight: 3,
  color: '#b91c1c',
  fillColor: '#ef4444',
  fillOpacity: 0.8
};

// Escapes the four characters that matter inside a popup built as HTML.
//
// The names come from the database, so they are somebody's input: a customer
// called `<img onerror=...>` would otherwise run script in the popup. Leaflet's
// bindPopup takes HTML, which is what makes this necessary - the link below is
// why plain text is not an option.
function escapeHtml(wert) {
  return String(wert == null ? '' : wert)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// One popup: who they are, where, how long the journey takes, and a link when
// the viewer could reach them.
//
// The link mirrors the table exactly - a row the viewer cannot reach renders as
// plain text there and must not become clickable here, or the map would be the
// way around a rule the list respects. See darfKundeSehen in
// utils/permissions.js.
function popupHtml(eintrag) {
  const name = escapeHtml(eintrag.name);
  const kopf = eintrag.sichtbar
    ? `<a href="/kunden/${encodeURIComponent(eintrag.id)}">${name}</a>`
    : `<strong>${name}</strong>`;

  const zeilen = [eintrag.ort, eintrag.minuten == null ? null : `${eintrag.minuten} min`]
    .filter(Boolean)
    .map((wert) => escapeHtml(wert))
    .join(' · ');

  return zeilen ? `${kopf}<br>${zeilen}` : kopf;
}

function initErreichbarLayer(karte, mitte) {
  const region = document.getElementById(ERREICHBAR_REGION);
  if (!region) return;

  // A cluster group rather than a plain layer group, which is the one real
  // difference from the customer map: a candidate in the Mittelland reaches
  // hundreds of customers, and single pins would pile into an unreadable blob.
  // Added to the map once, then emptied and refilled, so a redraw never touches
  // the map's own layer list.
  const ebene = L.markerClusterGroup({
    // Leaflet's default spiderfies a cluster that cannot be split further. Kept
    // on: at the tightest zoom several customers genuinely share one building.
    showCoverageOnHover: false,
    maxClusterRadius: 50
  }).addTo(karte);

  function zeichne() {
    ebene.clearLayers();

    const traeger = region.querySelector('[data-erreichbar-marker]');
    if (!traeger) return;

    let marker = [];
    try {
      marker = JSON.parse(traeger.dataset.erreichbarMarker) || [];
    } catch {
      // A malformed payload leaves the map with the candidate's own pin and no
      // customers, which is the same picture somebody with nothing in range
      // gets. Swallowed rather than thrown: the table beside it is correct and
      // readable, and taking main.js down over a pin is worse.
      return;
    }

    const punkte = [];

    marker.forEach((eintrag) => {
      const breite = Number(eintrag.lat);
      const laenge = Number(eintrag.lng);
      if (!Number.isFinite(breite) || !Number.isFinite(laenge)) return;

      // [lat, lng] here: Leaflet takes latitude first, the opposite of the
      // GeoJSON order the server sends. Written out rather than spread, so the
      // swap cannot happen by accident.
      punkte.push([breite, laenge]);
      L.marker([breite, laenge]).bindPopup(popupHtml(eintrag)).addTo(ebene);
    });

    // Frame the whole reachable set plus the candidate, so the first paint
    // answers "how far does this person's world reach" without anybody
    // zooming. Only when there is something to frame: fitBounds on an empty
    // list throws, and a candidate with nothing in range should simply stay
    // centred on their own address.
    if (punkte.length > 0) {
      karte.fitBounds(L.latLngBounds([...punkte, mitte]).pad(0.1));
    }
  }

  zeichne();

  // Every swap, whatever triggered it. childList on the region is enough:
  // liveList replaces innerHTML, so the carrier element is a direct child that
  // gets removed and re-added wholesale.
  new MutationObserver(zeichne).observe(region, { childList: true });
}

export function initKandidatKarte() {
  const container = document.getElementById('kandidat-karte');
  if (!container) return;

  const { lng, lat, token, name } = container.dataset;

  const laenge = Number(lng);
  const breite = Number(lat);

  // The template only renders this element with both coordinates set, so a
  // non-numeric pair here means something upstream is wrong rather than a
  // candidate simply not being geocoded. Bail rather than ask Leaflet to centre
  // on NaN, which throws inside the library and takes the rest of main.js down.
  if (!Number.isFinite(laenge) || !Number.isFinite(breite)) return;

  // Without a public token there is nothing to authenticate the tile requests
  // with. The template shows its own hint in that case; this is the guard for
  // the element existing anyway.
  if (!token) return;

  // Leaflet resolves its default marker images relative to the script that
  // loaded it, which is /js/bundle.js - so it would look for /js/images/. The
  // images are vendored beside the stylesheet instead, by scripts/copyIcons.js.
  L.Icon.Default.imagePath = '/vendor/leaflet/images/';

  const mitte = [breite, laenge];

  const karte = L.map(container, {
    center: mitte,
    zoom: ZOOM,
    // The page scrolls; a wheel over an embedded map that swallows the scroll
    // is a well-known annoyance. Dragging and the +/- control still work.
    scrollWheelZoom: false
  });

  L.tileLayer(TILE_URL, {
    accessToken: token,
    attribution: ATTRIBUTION,
    maxZoom: 19
  }).addTo(karte);

  // The candidate's own address, as a circle rather than a default pin so it
  // stays distinguishable from the clustered customer markers around it.
  const eigen = L.circleMarker(mitte, KANDIDAT_MARKER).addTo(karte);
  if (name) {
    eigen.bindPopup(name);
  }

  // The reachable customers, layered over the candidate's pin. Behind its own
  // element guard rather than a second init<Thing>(): the layer lives on *this*
  // map instance, and a separate module would have to be handed it - a shared
  // mutable across modules, or a second map on the same div.
  initErreichbarLayer(karte, mitte);

  // The section is inside a card that the browser lays out after this runs in
  // some paths, which leaves Leaflet believing the container is 0px tall and
  // drawing a single tile in the corner. Recalculating once on the next frame
  // is the standard fix.
  requestAnimationFrame(() => karte.invalidateSize());
}
